import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, UrlTree} from '@angular/router';
import {combineLatest, Observable, of} from 'rxjs';
import {catchError, filter, map, switchMap, take} from 'rxjs/operators';
import {SettingsService} from "../common/services/settings.service";
import {AppSettings} from "../common/models/appSettings";
import {AuthService} from "../services/auth.service";

@Injectable({
    providedIn: 'root'
})
export class FeatureFlagGuard implements CanActivate {

    constructor(
        private settingsService: SettingsService,
        private authService: AuthService,
        private router: Router
    ) {
    }

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
        const featureName = route.data['featureName'];
        const requiresAdmin = route.data['requiresAdmin'] || false;

        return this.settingsService.loadAllSettings().pipe(
            switchMap(() => combineLatest([
                this.settingsService.appSettings$.pipe(
                    filter((settings: AppSettings[] | null) => settings !== null),
                    take(1)
                ),
                this.authService.getUserInfo().pipe(
                    take(1)
                )
            ])),
            map(([settings, user]) => {
                if (!settings || !user) {
                    console.error('Settings or user data is missing.');
                    return this.router.createUrlTree(['/home']);
                }

                const adminRoles = ['admin', 'sys_admin'];
                const userGroup = user.group?.toLowerCase() ?? '';
                const isUserAdmin = adminRoles.includes(userGroup);

                const featureSetting = settings.find(s => s.featureName?.toLowerCase() === featureName?.toLowerCase());
                const isFeatureEnabled = featureSetting?.isEnabled ?? false;

                if (!isFeatureEnabled) {
                    console.warn(`Feature '${featureName}' is disabled.`);
                    return this.router.createUrlTree(['/home']);
                }

                if (requiresAdmin && !isUserAdmin) {
                    console.warn(`User is not an admin and cannot access '${featureName}'.`);
                    return this.router.createUrlTree(['/home']);
                }

                return true;
            }),
            catchError((error) => {
                console.error('Error during route guard check:', error);
                return of(this.router.createUrlTree(['/home']));
            })
        );
    }
}